import React, { useEffect } from 'react';

/**
 * Modal Component - Reusable modal dialog
 */
export function Modal({ isOpen, onClose, title, children, maxWidth = 'max-w-4xl' }) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="modal-backdrop">
      <div className={`bg-white rounded-xl shadow-2xl w-full ${maxWidth} max-h-[95vh] overflow-hidden flex flex-col`}>
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 text-2xl w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
            data-testid="modal-close-btn"
          >
            &times;
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

/**
 * Toast Notification Component
 */
export function Toast({ message, type, onClose, duration = 3000 }) {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [onClose, duration]);

  const bgColor = type === 'success' ? 'bg-green-500' : 
                  type === 'error' ? 'bg-red-500' : 
                  type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500';

  return (
    <div 
      className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${bgColor} text-white`}
      data-testid="toast-notification"
    >
      {message}
    </div>
  );
}

/**
 * StatCard Component - Dashboard statistics card
 */
export function StatCard({ title, value, icon, color = 'orange', subtitle, onClick }) {
  const colors = {
    orange: 'bg-gradient-to-br from-orange-500 to-amber-500',
    green: 'bg-gradient-to-br from-green-500 to-emerald-500',
    blue: 'bg-gradient-to-br from-blue-500 to-indigo-500',
    purple: 'bg-gradient-to-br from-purple-500 to-violet-500',
    red: 'bg-gradient-to-br from-red-500 to-rose-500',
    teal: 'bg-gradient-to-br from-teal-500 to-cyan-500'
  };

  return (
    <div 
      className={`${colors[color]} text-white rounded-xl p-4 shadow-lg ${onClick ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
      onClick={onClick}
      data-testid={`stat-card-${title?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs opacity-80">{title}</p>
          <p className="text-2xl font-bold mt-1">{typeof value === 'number' ? value.toLocaleString() : value}</p>
          {subtitle && <p className="text-xs opacity-70 mt-1">{subtitle}</p>}
        </div>
        <span className="text-3xl opacity-60">{icon}</span>
      </div>
    </div>
  );
}

/**
 * Loading Spinner Component
 */
export function LoadingSpinner({ size = 'md', color = 'orange' }) {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  const colors = {
    orange: 'border-orange-500',
    blue: 'border-blue-500',
    white: 'border-white'
  };

  return (
    <div className={`animate-spin rounded-full ${sizes[size]} border-b-2 ${colors[color]}`} />
  );
}

/**
 * Empty State Component
 */
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="text-center py-12" data-testid="empty-state">
      <div className="text-6xl mb-4 opacity-50">{icon}</div>
      <h3 className="text-lg font-semibold text-gray-600 mb-2">{title}</h3>
      {description && <p className="text-gray-500 mb-4">{description}</p>}
      {action}
    </div>
  );
}

export default { Modal, Toast, StatCard, LoadingSpinner, EmptyState };
